import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import { fetchTaskDetails, updateTask, completeTask, addTaskComment, uploadTaskAttachment, deleteTask, archiveTask, removeTaskAttachment, addTaskCollaborator, removeTaskCollaborator, updateCollaboratorPrivilege, listTaskComments, listTaskAttachments, listTaskCollaborators, listTaskRelatedItems, removeTaskRelatedItem, fetchTaskFeed, postTaskFeedEvent, addTaskFeedComment } from '../../store/actions/TasksActions';
import { useGlobalStreaming } from '../../hooks/useGlobalStreaming';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../shared/TailwindComponents/Button';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { getUserType } from '../../store/actions/ChatAction';
import dayjs from 'dayjs';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { useTranslation } from 'react-i18next';
import { PiCheckLight, PiTrashLight, PiClockClockwiseLight, PiFileTextLight, PiImageLight, PiFile, PiCloudArrowDownLight, PiArchiveLight, PiCheckCircleLight, PiFileLight, PiChatCircleTextLight, PiUsersLight, PiPaperclipLight, PiBriefcaseLight, PiCalendarBlankLight, PiFlagLight, PiDotsThreeVerticalLight } from "react-icons/pi";
import ProfileImage from '../Header/ProfileImage';
import Badge from '../shared/TailwindComponents/Badge';
import { formatDate, formatDateTime, formatStatus, formatBytes } from '../../utils/functions';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import DetailFrame from '../shared/Frames/DetailFrame';
import TaskDetailsContent from './TaskDetailsContent';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, taskDetailTourSteps } from '../shared/FeatureTour';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-fadePulse bg-gray-200 dark:bg-gray-700 rounded ${className}`}></div>
);

const SkeletonLoader: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-6 w-20" />
    </div>
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

interface TaskDetailParams {
  taskUuid: string;
}

const normalizeAttachment = (attachment: any, taskUuid?: string): any => {
  const attachmentId = attachment.attachment_id || attachment.AttachmentID || attachment.id;
  const fileUrl = attachment.FileURL ||
    (taskUuid && attachmentId ? `/tasks/task/${taskUuid}/attachments/${attachmentId}/download` : attachment.blob_path);

  const filename = attachment.filename || attachment.FileName || '';
  const fileExtension = filename.includes('.') ? filename.split('.').pop() || 'file' : 'file';

  const contentType = attachment.content_type || attachment.FileType || '';
  const fileType = contentType || fileExtension;

  return {
    AttachmentID: attachmentId,
    id: attachmentId,
    FileName: filename,
    FileType: fileType,
    ContentType: contentType,
    FileURL: fileUrl,
    blobPath: attachment.blob_path,
    size: attachment.size,
    uploadedAt: attachment.uploaded_at || attachment.CreationDate,
    uploadedBy: attachment.uploaded_by_user || null,
  };
};

const normalizeComment = (comment: any, usersMap?: Record<number, any>): any | null => {
  if (!comment || !comment.comment_text) {
    return null;
  }

  const createdByObject = comment.created_by || {};
  const createdByUserId = comment.created_by_user_id || createdByObject.id;

  const firstName = createdByObject.first_name || '';
  const lastName = createdByObject.last_name || '';
  const email = createdByObject.email || 'unknown@example.com';

  const initials = `${(firstName || '').charAt(0).toUpperCase()}${(lastName || '').charAt(0).toUpperCase()}`.trim() || 'U';
  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';

  return {
    uuid: comment.uuid,
    CommentText: comment.comment_text || comment.CommentText,
    UserName: fullName,
    UserEmail: email,
    Timestamp: comment.created_at || comment.Timestamp,
    ProfilePicture: createdByObject.profile_picture || null,
    Initials: initials,
    is_pinned: comment.is_pinned,
    is_edited: comment.is_edited,
    created_by_user_id: createdByUserId,
  };
};

const normalizeCollaborator = (collaborator: any, usersMap?: Record<number, any>): any => {
  const name = collaborator.name || '';
  const email = collaborator.email || 'unknown@example.com';
  const userId = collaborator.collaborator_id || collaborator.id || collaborator.user_id;

  let initials = collaborator.initials;
  if (!initials && name) {
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      initials = `${nameParts[0].charAt(0).toUpperCase()}${nameParts[nameParts.length - 1].charAt(0).toUpperCase()}`;
    } else {
      initials = nameParts[0]?.charAt(0).toUpperCase() || 'U';
    }
  }
  initials = initials || 'U';

  const firstName = collaborator.first_name || '';
  const lastName = collaborator.last_name || '';
  const legacyInitials = `${(firstName || '').charAt(0).toUpperCase()}${(lastName || '').charAt(0).toUpperCase()}`.trim() || 'U';
  const legacyFullName = `${firstName} ${lastName}`.trim() || 'Unknown';

  const fullName = name || legacyFullName;
  const displayInitials = name ? initials : legacyInitials;

  const access = collaborator.access || (collaborator.role === 'editor' || collaborator.role === 'admin' ? 'edit' : 'view');
  const role = collaborator.role || 'collaborator';

  const addedByObj = collaborator.added_by || {};
  const addedByName = addedByObj.name || `${addedByObj.first_name || ''} ${addedByObj.last_name || ''}`.trim() || 'Unknown';
  const addedByEmail = addedByObj.email || '';
  const addedByUserId = collaborator.added_by_user_id || addedByObj.id;

  let addedByInitials = addedByObj.initials;
  if (!addedByInitials && addedByName) {
    const nameParts = addedByName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      addedByInitials = `${nameParts[0].charAt(0).toUpperCase()}${nameParts[nameParts.length - 1].charAt(0).toUpperCase()}`;
    } else {
      addedByInitials = nameParts[0]?.charAt(0).toUpperCase() || 'U';
    }
  }
  addedByInitials = addedByInitials || 'U';

  return {
    uuid: collaborator.collaborator_id || collaborator.id || collaborator.uuid,
    UserID: userId,
    Name: fullName,
    Email: email,
    ProfilePicture: collaborator.avatar_url || collaborator.user?.profile_picture || collaborator.profile_picture || null,
    Initials: displayInitials,
    access: access,
    role: role,
    CanCompleteOrArchive: access === 'edit' || access === 'admin' || role === 'editor' || role === 'admin',
    created_at: collaborator.created_at || collaborator.shared_at,
    shared_by: collaborator.shared_by,
    added_by: addedByEmail || addedByUserId ? {
      Name: addedByName,
      Email: addedByEmail,
      FirstName: addedByName.split(' ')[0] || '',
      LastName: addedByName.split(' ')[1] || '',
      Initials: addedByInitials,
      UserID: addedByUserId,
    } : null,
  };
};

const EMPTY_PEOPLE: any[] = [];

const TaskDetail: React.FC = () => {
  const { t } = useTranslation();
  const { taskUuid } = useParams<TaskDetailParams>();
  const dispatch = useAppDispatch();
  const userType = getUserType();
  const { setBreadcrumbs } = useBreadcrumbs();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const history = useHistory();
  const location = useLocation();

  const [isEditing, setIsEditing] = useState(false);
  const [userAccessLevel, setUserAccessLevel] = useState<'edit' | 'view' | 'owner' | 'admin'>('owner');
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [matterData, setMatterData] = useState<any>(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [matterLoading, setMatterLoading] = useState(false);
  const [newComment, setNewComment] = useState<string>('');
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState<string>('');
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isNewTask, setIsNewTask] = useState(false);
  const [taskFeed, setTaskFeed] = useState<any[]>([]);
  const [taskFeedLoading, setTaskFeedLoading] = useState(false);
  const employees = useSelector((state: RootState) => state.employees.employees);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'Details';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [matters, setMatters] = useState<any[]>([]);
  const [mattersLoading, setMattersLoading] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState<boolean>(false);
  const people = useSelector((state: RootState) => state.orgPeople?.people ?? EMPTY_PEOPLE);
  const [newCollaboratorAccess, setNewCollaboratorAccess] = useState<'edit' | 'view'>('view');
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set([]));

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-detail',
    steps: taskDetailTourSteps,
    readyToShow: !loading && !!taskDetails,
    autoStartDelay: 600,
  });

  const usersMap = useMemo(() => {
    const map: Record<number, any> = {};
    employees.forEach((emp: any) => {
      if (emp.id || emp.UserID) {
        map[emp.id || emp.UserID] = emp;
      }
    });
    return map;
  }, [employees]);

  const employeeOptions: DropdownOption[] = useMemo(() => people.map((person) => ({
    value: person.email,
    label: `${person.first_name} ${person.last_name}`,
  })), [people]);

  const renderCollaboratorOption = useCallback((option: DropdownOption): React.ReactNode => {
    const person = people.find(p => p.email === option.value);
    if (!person) {
      return <span className="text-xs">{option.label}</span>;
    }

    const initials = `${(person.first_name || '').charAt(0).toUpperCase()}${(person.last_name || '').charAt(0).toUpperCase()}`.trim() || 'U';

    return (
      <div className="flex items-center gap-3 w-full">
        <ProfileImage
          profilePicture={person.profile_picture || null}
          email={person.email || null}
          initials={initials}
          firstName={person.first_name}
          lastName={person.last_name}
          size="h-8 w-8"
          className="flex-shrink-0"
        />
        <span className="text-xs truncate">{person.first_name} {person.last_name}</span>
      </div>
    );
  }, [people]);

  // Fetch matters and people on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setMattersLoading(true);
        const mattersResponse = await dispatch(fetchMatters({
          page: 1,
          page_size: 100,
        }) as any);
        if (mattersResponse && mattersResponse.matters) {
          setMatters(mattersResponse.matters);
        }

        await dispatch(fetchOrgPeople() as any);
      } catch (error) {
      } finally {
        setMattersLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Fetch task details when taskUuid changes
  useEffect(() => {
    if (!taskUuid) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    dispatch(fetchTaskDetails(taskUuid) as any)
      .then((response: any) => {
        if (!isMounted) return;

        if (response && (response.id || response.uuid)) {
          setTaskDetails(response);
          // Handle both response formats (uppercase and lowercase)
          const collaboratorsData = response.Collaborators || response.collaborators || [];
          const commentsData = response.Comments || response.comments || [];
          const attachmentsData = response.Attachments || response.attachments || [];

          const normalizedComments = commentsData
            .map((comment: any) => normalizeComment(comment, usersMap))
            .filter((comment: any) => comment !== null);
          setComments(normalizedComments);

          const normalizedCollaborators = collaboratorsData.map((collab: any) =>
            normalizeCollaborator(collab, usersMap)
          );
          setCollaborators(normalizedCollaborators);

          const normalizedAttachments = attachmentsData.map((att: any) => normalizeAttachment(att, taskUuid));
          setAttachments(normalizedAttachments);

          const relatedMatters = response.related_items || [];
          setMatterData(relatedMatters.length > 0 ? relatedMatters[0] : null);

          const currentUserAsCreator = response.assigned_to_user_id === userInfo?.UserID || response.created_by_user_id === userInfo?.UserID;
          if (currentUserAsCreator) {
            setUserAccessLevel('owner');
          } else {
            const userCollaborator = collaboratorsData.find((collab: any) => {
              const collabUserId = collab.collaborator_user_id || collab.user?.id || collab.user_id;
              if (collabUserId === userInfo?.UserID) {
                return true;
              }
              if (collab.email === userInfo?.Email) {
                return true;
              }
              return false;
            });
            if (userCollaborator) {
              let access: 'edit' | 'view' | 'admin' = 'view';
              if (userCollaborator.access) {
                if (userCollaborator.access === 'admin') {
                  access = 'admin';
                } else if (userCollaborator.access === 'edit') {
                  access = 'edit';
                } else {
                  access = 'view';
                }
              } else if (userCollaborator.role === 'viewer') {
                access = 'view';
              } else if (userCollaborator.role === 'admin' || userCollaborator.role === 'creator') {
                access = 'edit';
              }
              setUserAccessLevel(access as any);
            }
          }
        } else {
          setAccessDenied(true);
          setTaskDetails(null);
        }
        setLoading(false);
      })
      .catch((err: any) => {
        if (!isMounted) return;
        setAccessDenied(true);
        setTaskDetails(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [taskUuid, dispatch]);

  const refetchTabData = useCallback(async (tab: string) => {
    if (!taskUuid) return;

    switch (tab) {
      case 'Feed':
        await loadTaskFeed();
        break;

      case 'Attachments':
        setAttachmentsLoading(true);
        try {
          const attachmentsData = await dispatch(listTaskAttachments(taskUuid) as any);
          if (attachmentsData?.attachments) {
            const normalizedAttachments = (attachmentsData.attachments || []).map((att: any) => normalizeAttachment(att, taskUuid));
            setAttachments(normalizedAttachments);
          }
        } catch (error) {
          console.error('Error fetching attachments:', error);
          // Preserve existing attachments on error
        } finally {
          setAttachmentsLoading(false);
        }
        break;

      case 'Details':
        setCollaboratorsLoading(true);
        try {
          const collaboratorsData = await dispatch(listTaskCollaborators(taskUuid) as any);
          if (collaboratorsData?.collaborators) {
            const normalizedCollaborators = (collaboratorsData.collaborators || []).map((collab: any) =>
              normalizeCollaborator(collab, usersMap)
            );
            setCollaborators(normalizedCollaborators);
          }
        } catch (error) {
          console.error('Error fetching collaborators:', error);
          // Preserve existing collaborators on error
        } finally {
          setCollaboratorsLoading(false);
        }
        break;
    }
  }, [taskUuid, dispatch, usersMap]);

  // Fetch tab-specific data on first tab open (not on every tab change)
  useEffect(() => {
    if (!taskUuid || !activeTab || loadedTabs.has(activeTab)) return;

    const fetchTabData = async () => {
      try {
        if (activeTab === 'Feed' && taskFeed.length === 0) {
          await loadTaskFeed();
        } else if (activeTab === 'Attachments' && attachments.length === 0) {
          setAttachmentsLoading(true);
          const attachmentsData = await dispatch(listTaskAttachments(taskUuid) as any);
          if (attachmentsData?.attachments) {
            const normalizedAttachments = (attachmentsData.attachments || []).map((att: any) => normalizeAttachment(att, taskUuid));
            setAttachments(normalizedAttachments);
          }
          setAttachmentsLoading(false);
        }
      } catch (error) {
        console.error(`Error fetching ${activeTab} data:`, error);
      }

      // Mark tab as loaded
      setLoadedTabs(prev => new Set(prev).add(activeTab));
    };

    fetchTabData();
  }, [activeTab, taskUuid, dispatch]);

  const matterOptions: DropdownOption[] = useMemo(() => matters.map((matter: any) => ({
    value: matter.uuid || matter.id || matter.MatterID,
    label: matter.title || matter.MatterName || `${t('Matter')} ${matter.id || matter.MatterID}`,
  })), [matters, t]);

  const priorityOptions: DropdownOption[] = useMemo(() => [
    { value: 'low', label: t('Low') },
    { value: 'medium', label: t('Medium') },
    { value: 'high', label: t('High') },
  ], [t]);

  useEffect(() => {
    if (taskDetails) {
      setBreadcrumbs([
        { name: t('Tasks'), href: '/tasks', current: false },
        { name: taskDetails.title || t('Task Details'), href: `/tasks/${taskUuid}`, current: true },
      ]);
    } else {
      setBreadcrumbs([
        { name: t('Tasks'), href: '/tasks', current: false },
        { name: t('Loading...'), href: `/tasks/${taskUuid}`, current: true },
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDetails?.title, taskUuid, setBreadcrumbs]);

  const refetchTaskDetails = useCallback(() => {
    if (!taskUuid) return;

    setLoading(true);
    setError(null);

    dispatch(fetchTaskDetails(taskUuid) as any)
      .then((response: any) => {
        if (response && (response.id || response.uuid)) {
          setTaskDetails(response);
          const normalizedComments = (response.Comments || [])
            .map((comment: any) => normalizeComment(comment, usersMap))
            .filter((comment: any) => comment !== null);
          setComments(normalizedComments);
          
          // Only update collaborators if we have new data, otherwise preserve existing
          if (response.Collaborators && response.Collaborators.length > 0) {
            const normalizedCollaborators = (response.Collaborators || []).map((collab: any) =>
              normalizeCollaborator(collab, usersMap)
            );
            setCollaborators(normalizedCollaborators);
          }
          
          const normalizedAttachments = (response.Attachments || []).map((att: any) => normalizeAttachment(att, taskUuid));
          setAttachments(normalizedAttachments);
          const relatedMatters = response.related_items || [];
          setMatterData(relatedMatters.length > 0 ? relatedMatters[0] : null);
        } else {
          setError('Task not found');
          setTaskDetails(null);
        }
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('Error loading task details:', err);
        setError('Error loading task');
        // Preserve existing task details on error instead of clearing
        setLoading(false);
      });
  }, [taskUuid, dispatch, usersMap]);

  const validationSchema = useMemo(() =>
    Yup.object({
      title: Yup.string().required(t('titleRequired')),
      description: Yup.string().required(t('descriptionRequired')),
      due_date: Yup.string().nullable().notRequired(),
      priority: Yup.string().oneOf(['low', 'medium', 'high'], t('invalidPriority')).required(t('priorityRequired')),
    }),
  [t]);

  const onSubmitHandler = useCallback(async (values: any) => {
    try {
      const result = await dispatch(
        updateTask(taskUuid, {
          title: values.title,
          description: values.description,
          due_date: values.due_date,
          priority: values.priority,
        })
      );
      if (!result) return; // Action failed — handleApiError already showed the error toast
      dispatch(
        showFeedbackModal({
          modalType: 'success',
          message: t('Task updated successfully'),
          showModal: true,
          duration: 3000,
        })
      );
      setIsEditing(false);
      setHasChanges(false);
      // Refetch to get latest data, but preserve collaborators
      refetchTaskDetails();
    } catch (error: any) {
      console.error(t('Error updating task. Please try again later.'), error);
      dispatch(
        showFeedbackModal({
          modalType: 'error',
          message: t('Error updating task. Please try again later.'),
          showModal: true,
          duration: 3000,
        })
      );
    }
  }, [taskUuid, dispatch, t, refetchTaskDetails]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: taskDetails?.title || '',
      description: taskDetails?.description || '',
      due_date: taskDetails?.due_date ? new Date(taskDetails.due_date).toISOString() : '',
      priority: taskDetails?.priority || 'medium',
    },
    validationSchema,
    onSubmit: onSubmitHandler,
  });

  const getStatusBadgeColor = (status: string | undefined | null) => {
    if (!status) return 'gray';
    switch (status.toLowerCase()) {
      case 'completed': return 'green';
      case 'todo': return 'gray';
      case 'active': return 'blue';
      case 'pending': return 'yellow';
      case 'deleted': return 'red';
      case 'archived': return 'red';
      default: return 'gray';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'amber';
      case 'critical': return 'red';
      default: return 'yellow';
    }
  };

  const handleChangeMatter = useCallback(async () => {
    if (!selectedCaseId) {
      dispatch(showFeedbackModal({ showModal: true, message: t('Error selecting matter. Please try again later.'), modalType: 'error', duration: 3000 }));
      return;
    }
    setMatterLoading(true);
    try {
      // Update the task's matter_uuid directly via the update endpoint
      const result = await dispatch(updateTask(taskUuid, { matter_uuid: selectedCaseId }) as any);
      if (!result) return; // Action failed — handleApiError already showed the error toast
      setSelectedCaseId('');
      // Reload full task details to refresh matter information
      await refetchTaskDetails();
      dispatch(showFeedbackModal({ showModal: true, message: t('Matter updated successfully'), modalType: 'success', duration: 3000 }));
    } catch (error: any) {
      console.error(t('Error updating matter. Please try again later.'), error);
    } finally {
      setMatterLoading(false);
    }
  }, [selectedCaseId, dispatch, taskUuid, t, refetchTaskDetails]);

  const handleComplete = useCallback(async () => {
    try {
      dispatch(
        showModal({
          type: 'addTaskComment',
          message: t('Complete Task'),
          icon: <PiCheckCircleLight className='w-6 h-6 text-green-600' />,
          subMsg: t('Add an optional comment to let others know about the completion.'),
          onConfirm: async (comment: string) => {
            setCommentsLoading(true);
            try {
              await dispatch(completeTask(taskUuid) as any);
              if (comment.trim()) {
                await dispatch(addTaskComment(taskUuid, comment) as any);
              }
              await refetchTaskDetails();
              dispatch(hideModal());
            } catch (error: any) {
              dispatch(showFeedbackModal({
                modalType: 'error',
                message: t('Error completing task. Please try again later.'),
                showModal: true,
                duration: 3000,
              }));
            } finally {
              setCommentsLoading(false);
            }
          },
          showModal: true,
          onCancel: () => dispatch(hideModal()),
          duration: 3000,
        })
      );
    } catch (error) {
      console.error(t('Error completing task. Please try again later.'), error);
      setCommentsLoading(false);
    }
  }, [dispatch, taskUuid, t, refetchTaskDetails]);

  const handleArchive = useCallback(async () => {
    try {
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          onConfirm: async () => {
            dispatch(archiveTask(taskUuid) as any);
            refetchTaskDetails();
            dispatch(hideModal());
          },
          message: t('Archive Task'),
          subMsg: t('This task will be archived. You can restore it later if needed.'),
          icon: <PiArchiveLight className='w-6 h-6 text-red-600' />,
        })
      );
    } catch (error) {
      console.error(t('Error archiving task. Please try again later.'), error);
    }
  }, [dispatch, taskUuid, t, refetchTaskDetails]);

  const handleRestore = useCallback(async () => {
    try {
      const result = await dispatch(updateTask({ TaskID: taskUuid, Status: 'todo' }) as any);
      if (!result) return; // Action failed — handleApiError already showed the error toast
      dispatch(showFeedbackModal({ modalType: 'success', message: t('Task restored.'), showModal: true, duration: 3000 }));
      refetchTaskDetails();
    } catch (error) {
      console.error(t('Error restoring task. Please try again later.'), error);
    }
  }, [dispatch, taskUuid, t, refetchTaskDetails]);

  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) return;
    const tempComment = {
      uuid: `temp-${Date.now()}`,
      CommentText: newComment,
      UserName: `${userInfo?.FirstName || ''} ${userInfo?.LastName || ''}`.trim(),
      UserEmail: userInfo?.Email,
      Initials: `${(userInfo?.FirstName || '').charAt(0).toUpperCase()}${(userInfo?.LastName || '').charAt(0).toUpperCase()}`.trim() || 'U',
      ProfilePicture: userInfo?.ProfilePicture,
      Timestamp: dayjs().toISOString(),
    };
    setComments(prev => [...prev, tempComment]);
    setNewComment('');
    try {
      await dispatch(addTaskComment(taskUuid, newComment) as any);
    } catch (error: any) {
      setComments(prev => prev.filter(c => c.uuid !== tempComment.uuid));
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error adding comment. Please try again later.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [newComment, userInfo, dispatch, taskUuid, t]);

  // ─── Task Feed (SQL-backed) handlers ───────────────────────────────────
  const loadTaskFeed = useCallback(async () => {
    setTaskFeedLoading(true);
    try {
      const result = await dispatch(fetchTaskFeed(taskUuid) as any);
      if (Array.isArray(result)) {
        setTaskFeed(result);
      }
    } finally {
      setTaskFeedLoading(false);
    }
  }, [dispatch, taskUuid]);

  const handlePostTaskFeedEvent = useCallback(async (eventData: { event_type: string; message: string; metadata?: any }) => {
    await dispatch(postTaskFeedEvent(taskUuid, eventData) as any);
    await loadTaskFeed();
  }, [dispatch, taskUuid, loadTaskFeed]);

  const handleAddTaskFeedComment = useCallback(async (eventId: string, comment: string) => {
    await dispatch(addTaskFeedComment(taskUuid, eventId, comment) as any);
    await loadTaskFeed();
  }, [dispatch, taskUuid, loadTaskFeed]);

  const handleFileUpload = useCallback(async (files: File[]) => {
    setAttachmentsLoading(true);
    try {
      for (const file of files) {
        await dispatch(uploadTaskAttachment(taskUuid, file) as any);
      }
      await refetchTabData('Attachments');
    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error uploading file. Please try again later.'),
        showModal: true,
        duration: 3000,
      }));
    } finally {
      setAttachmentsLoading(false);
    }
  }, [dispatch, taskUuid, t, refetchTabData]);

  const handleOpenUploadModal = useCallback(() => {
    dispatch(
      showSidePanel({
        type: 'uploadAttachment',
        showSidePanel: true,
        width: '40%',
        message: t('Upload Attachments'),
        icon: <PiCloudArrowDownLight className='w-5 h-5 text-gray-600' />,
        onConfirm: async (data: any) => {
          if (data?.files?.length) {
            await handleFileUpload(data.files);
          }
        },
        onCancel: () => dispatch(hideSidePanel()),
      } as any)
    );
  }, [dispatch, t, handleFileUpload]);

  const { viewFile } = useGlobalStreaming();

  const handleOpenAttachment = useCallback(async (attachment: any) => {
    const fileType = (attachment.FileType || '').toLowerCase();
    const attachmentId = attachment.AttachmentID || attachment.id;
    const filename = attachment.FileName;

    try {
      const result = await viewFile({
        domain: 'tasks',
        resourceId: taskUuid,
        fileId: attachmentId,
        filename: filename,
        contentType: fileType,
      });

      if (!result.success || !result.blob) {
        console.error('[handleOpenAttachment] Failed to fetch attachment', result);
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: result.error || t('Failed to load attachment'),
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      dispatch(
        showSidePanel({
          type: 'reader',
          showSidePanel: true,
          width: '60%',
          message: filename,
          icon: <PiFileLight className='w-5 h-5 text-gray-600' />,
          citation: {
            title: filename,
            name: filename,
            blob: result.blob,
            blobObject: result.blob,
            contentType: result.contentType || fileType,
            type: 'file',
            id: attachmentId,
          },
          matterId: taskUuid,
          sourceType: 'file',
          userAccessLevel,
          onCancel: () => dispatch(hideSidePanel()),
        } as any)
      );
    } catch (error: any) {
      console.error('[handleOpenAttachment] Error opening attachment:', error);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error opening attachment. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [dispatch, taskUuid, viewFile]);

  const handleDeleteCollaborator = useCallback((collaboratorUuid: string) => {
    dispatch(showModal({
      type: 'confirmation',
      onConfirm: async () => {
        try {
          await dispatch(removeTaskCollaborator(taskUuid, collaboratorUuid) as any);
          await refetchTabData('Details');
          dispatch(hideModal());
        } catch (error: any) {
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Error removing collaborator. Please try again later.'),
            showModal: true,
            duration: 3000,
          }));
          dispatch(hideModal());
        }
      },
      showModal: true,
      message: t('Remove Collaborator'),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      subMsg: t('This collaborator will lose access to this task. This action cannot be undone.'),
      isDest: true
    }))
  }, [dispatch, taskUuid, t, refetchTabData]);

  const handleDeleteAttachment = useCallback((attachmentId: string) => {
    dispatch(showModal({
      type: 'confirmation',
      onConfirm: async () => {
        try {
          await dispatch(removeTaskAttachment(taskUuid, attachmentId) as any);
          await refetchTabData('Attachments');
          dispatch(hideModal());
        } catch (error: any) {
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Error deleting attachment. Please try again later.'),
            showModal: true,
            duration: 3000,
          }));
          dispatch(hideModal());
        }
      },
      showModal: true,
      message: t('Remove Attachment'),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      subMsg: t('This attachment will be permanently removed from the task. This action cannot be undone.'),
      isDest: true
    }))
  }, [dispatch, taskUuid, t, refetchTabData]);

  const taskStatus = (taskDetails?.status || 'todo').toLowerCase();

  const availableEmployees = employeeOptions.filter(
    (emp) =>
      !collaborators.some((collab: any) => collab.Email === emp.value) &&
      emp.value !== userInfo?.Email
  );

  const clientOption: any[] = [];
  const collaboratorOptions = [...availableEmployees, ...clientOption];

  const tabs: any[] = useMemo(
    () => [
      {
        label: t('Details'),
        value: 'Details',
        icon: <PiFileTextLight className="h-4 w-4" />,
      },
      {
        label: t('Feed'),
        value: 'Feed',
        icon: <PiChatCircleTextLight className='h-4 w-4' />,
      },
      {
        label: t('Attachments'),
        value: 'Attachments',
        icon: <PiPaperclipLight className='h-4 w-4' />,
      },
    ],
    [t]
  );

  const taskBreadcrumbs = useMemo(
    () => [
      { name: t('Tasks'), href: '/tasks', current: false },
      {
        name: taskDetails?.title || t('Task Details'),
        href: `/tasks/${taskUuid}`,
        current: true,
      },
    ],
    [taskDetails?.title, taskUuid, t]
  );

  const taskActions = useMemo(() => {
    if (loading || !taskDetails) return [];
    const actions: { label: string; onClick: () => void; icon?: React.ReactElement }[] = [];
    if (taskStatus === 'todo' && userAccessLevel !== 'view') {
      actions.push({ label: t('Complete'), onClick: handleComplete, icon: <PiCheckLight className="w-4 h-4" /> });
    }
    if (taskStatus !== 'todo') {
      actions.push({ label: t('Restore'), onClick: handleRestore, icon: <PiClockClockwiseLight /> });
    }
    return actions;
  }, [loading, taskDetails, taskStatus, userAccessLevel, handleComplete, handleRestore, t]);

  const headerDropdownActions = useMemo(() => {
    const actions: { label: string; onClick: () => void; icon: React.ReactElement; variant?: 'neutral' | 'outline' | 'destructive' | 'primary' }[] = [];

    if (userAccessLevel !== 'view') {
      actions.push({
        label: t('Manage Access'),
        onClick: () => {
          const panelCollaborators = collaborators.map((c: any) => ({
            user_id: c.UserID || c.user_id,
            collaborator_uuid: c.uuid || c.collaborator_uuid,
            name: c.Name || c.name || '',
            email: c.Email || c.email || '',
            initials: c.Initials || c.initials || '',
            profile_picture: c.ProfilePicture || c.profile_picture || null,
            access: c.access || 'view',
            role: c.role,
            shared_at: c.created_at || c.shared_at,
            shared_by: c.shared_by,
            shared_by_user: c.added_by ? {
              name: c.added_by.Name || c.added_by.name,
              full_name: c.added_by.Name || c.added_by.name,
              email: c.added_by.Email || c.added_by.email,
            } : c.shared_by_user,
            source: 'direct',
          }));
          dispatch(showSidePanel({
            type: 'manageResourceCollaborators',
            showSidePanel: true,
            width: '50%',
            message: t('Manage Access'),
            resourceUuid: taskUuid,
            resourceType: 'task',
            collaborators: panelCollaborators,
            currentUserUuid: userInfo?.UserUID,
            canAdmin: true,
            addCollaborator: async (uuid: string, userUuid: string, access: string) => {
              await dispatch(addTaskCollaborator(uuid, userUuid, undefined, access as any) as any);
            },
            removeCollaborator: async (uuid: string, collabUuid: string) => {
              await dispatch(removeTaskCollaborator(uuid, collabUuid) as any);
            },
            updateAccess: async (uuid: string, collabUuid: string, access: string) => {
              await dispatch(updateCollaboratorPrivilege(uuid, collabUuid, access) as any);
            },
            onUpdate: async () => {
              await refetchTabData('Details');
            },
          }));
        },
        icon: <PiUsersLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline',
      });
    }

    actions.push({
      label: t('Activity Log'),
      onClick: () => setShowAuditLog((prev) => !prev),
      icon: <PiClockClockwiseLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
      variant: 'outline',
    });

    if (taskStatus === 'todo' && userAccessLevel !== 'view') {
      actions.push({
        label: t('Archive'),
        onClick: handleArchive,
        icon: <PiArchiveLight className="w-4 h-4 text-red-900" />,
        variant: 'destructive',
      });
    }

    return actions;
  }, [userAccessLevel, t, dispatch, taskUuid, collaborators, userInfo?.UserUID, taskStatus, handleComplete, handleArchive, refetchTabData]);

  const additionalHeaderElements = useMemo(() => (
    <div className="flex gap-3 items-center" data-tour-id="task-detail-collaborators">
      <ProjectBadges resourceType="task" resourceUuid={taskUuid} />
      <PendingAccessRequests resourceType="task" resourceUuid={taskUuid} />
      {collaborators?.length > 0 && (
        <div className="flex items-center">
          {collaborators.slice(0, 3).map((collab: any, idx: number) => (
            <div key={idx} className={clsx('relative rounded-full', idx > 0 && 'ml-[-0.5rem]')}>
              <CollaboratorBadge collaborator={{
                name: collab.Name || collab.name || '',
                initials: collab.Initials || collab.initials || 'U',
                email: collab.Email || collab.email || '',
                avatarURL: collab.ProfilePicture || collab.avatar_url || null,
                access: collab.access,
              }} />
            </div>
          ))}
          {collaborators.length > 3 && (
            <div className="relative ml-[-0.5rem]" title={collaborators.slice(3).map((c: any) => `${c.Name || c.name || ''} (${c.access || 'view'})`).join(', ')}>
              <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm text-gray-700 dark:text-white font-medium">
                +{collaborators.length - 3}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  ), [collaborators, taskUuid]);

  const subHeaderRight = useMemo(() => taskDetails ? (
    <div className="flex flex-wrap gap-2 items-center" data-tour-id="task-detail-badges">
      <Badge
        color={getStatusBadgeColor(taskStatus)}
        icon={<PiCheckLight />}
        className="capitalize"
      >
        {taskStatus === 'deleted' ? t('ARCHIVED') : formatStatus(taskStatus)}
      </Badge>
      <Badge
        color={getPriorityBadgeColor(taskDetails.priority)}
        icon={<PiFlagLight />}
      >
        {taskDetails.priority ? formatStatus(taskDetails.priority) : t('Medium')}
      </Badge>
      <Badge
        color="gray"
        icon={<PiCalendarBlankLight />}
      >
        {taskDetails.due_date
          ? formatDateTime(taskDetails.due_date)
          : t('No Due Date')}
      </Badge>
    </div>
  ) : null, [taskDetails, taskStatus, t]);

  const handleTabClick = useCallback((tabValue: string) => {
    setActiveTab(tabValue);
    history.push(`/tasks/${taskUuid}?tab=${tabValue}`);
  }, [history, taskUuid]);

  return (
    <DetailFrame
      breadcrumbs={taskBreadcrumbs}
      actions={taskActions}
      dropdownActions={headerDropdownActions}
      renderDropdownAsButtons={false}
      subscriptionActive={subscriptionActive}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
      additionalHeaderElements={additionalHeaderElements}
      notFound={accessDenied}
    >
      {loading ? (
        <SkeletonLoader />
      ) : !taskDetails ? null : (
        <div className="flex-1 flex flex-col">
          <div className="dark:bg-gray-800 w-full relative overflow-visible z-10" data-tour-id="task-detail-tabs">
            <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} />
          </div>
          <div className="pt-2 h-[calc(100vh-150px)] overflow-y-auto custom-scrollbar-main flex-1 pb-6">
            <TaskDetailsContent
              activeTab={activeTab}
              taskDetails={taskDetails}
              taskUuid={taskUuid}
              userAccessLevel={userAccessLevel}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              formik={formik}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              onSubmit={() => formik.handleSubmit()}
              taskStatus={taskStatus}
              comments={comments}
              setComments={setComments}
              collaborators={collaborators}
              setCollaborators={setCollaborators}
              attachments={attachments}
              setAttachments={setAttachments}
              matterData={matterData}
              setMatterData={setMatterData}
              commentsLoading={commentsLoading}
              collaboratorsLoading={collaboratorsLoading}
              attachmentsLoading={attachmentsLoading}
              matterLoading={matterLoading}
              newComment={newComment}
              setNewComment={setNewComment}
              selectedCaseId={selectedCaseId}
              setSelectedCaseId={setSelectedCaseId}
              matters={matters}
              refetchTabData={refetchTabData}
              handleAddComment={handleAddComment}
              handleOpenUploadModal={handleOpenUploadModal}
              handleFileUpload={handleFileUpload}
              handleOpenAttachment={handleOpenAttachment}
              handleDeleteCollaborator={handleDeleteCollaborator}
              handleDeleteAttachment={handleDeleteAttachment}
              handleChangeMatter={handleChangeMatter}
              onOpenAuditLog={() => setShowAuditLog(true)}
              taskFeed={taskFeed}
              taskFeedLoading={taskFeedLoading}
              onRefreshFeed={loadTaskFeed}
              onPostFeedEvent={handlePostTaskFeedEvent}
              onFeedCommentAdd={handleAddTaskFeedComment}
              currentUser={userInfo ? {
                id: userInfo.UserID || 'current-user',
                first_name: userInfo.FirstName || 'You',
                last_name: userInfo.LastName || '',
                email: userInfo.Email || '',
              } : undefined}
              feedMembers={(collaborators || []).map((c: any) => ({
                id: c.Email || c.id,
                first_name: c.Name?.split(' ')[0] || c.first_name || '',
                last_name: c.Name?.split(' ').slice(1).join(' ') || c.last_name || '',
                email: c.Email || c.email || '',
              }))}
            />
          </div>
        </div>
      )}
      <FeatureTour
        steps={taskDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />

      {showAuditLog && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
            onClick={() => setShowAuditLog(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType="task"
              entityId={taskUuid}
              entityName={taskDetails?.title || ''}
              onClose={() => setShowAuditLog(false)}
            />
          </div>
        </>
      )}
    </DetailFrame>
  );
};

export default TaskDetail;
